import { useState, useMemo, useCallback } from 'react';
import useList from 'react-use-v13/lib/useList';
import _ from 'lodash';
import { useBoolean } from 'react-use-v13';
import {
  getNodeChildren,
  getPreorderWorkflowTraversal,
  TaskerWorkflow,
  TraversedTaskerWorkflowStep,
  getWorkflowAncestors,
} from '@scale/shared/taskerWorkflow/TaskerWorkflow';
import { getContextKeys, getContextValue, logEvent } from '@scale/frontend-lib/events';
import { GuidedWorkflowActionTypes } from '@scale/shared/types/events/GuidedWorkflowActions';
import { Event } from '@scale/shared/types/events/index';
import { getFriendlyAnnotationId } from '@scale/shared/annotation';

export interface WorkflowHook {
  workflow: TaskerWorkflow | undefined;
  /**
   * Set and refresh the current workflow state to check for inconsistencies
   * When a new workflow is set, Ids may have changed between previous and current versions.
   * Refresh fixes any expected inconsistencies
   *
   * Cases:
   * - If the activeStep was deleted, reset to closest common ancestor
   * - If a completedStep was deleted, remove it from the completedStepIds
   * - If a newAnnotation was added, make sure it resets it's ancestors and children
   *
   * Note - Label changes count as a deletion and an addition
   */
  updateWorkflow: (newWorkflow: TaskerWorkflow | undefined) => void;
  workflowState: {
    completedStepIds: string[];
    uncollapsedStepIds: string[];
    activeStepId: string;
    currentStep: TraversedTaskerWorkflowStep;
    isGuidedWorkflowInstructionsOpen: boolean;
    isGuidedWorkflowInstructionsPanelOpen: boolean;
    isGuidedWorkflowTooltipOpen: boolean;

    // CheckEveryStates
    activeCheckEveryAnnotation: string | null;
    lockedCheckEveryAnnotation: string | null;
  };

  useGuidedWorkflowBar: boolean;
  setUseGuidedWorkflowBar: (nextValue?: any) => void;
  /**
   * Completes current step and moves to next incomplete step.
   * Will avoid direct descendents since they will also be completed
   * If the currentStep is completed, it will uncomplete the step.
   */
  completeAndMoveNext: () => void;

  /**
   * moves active step to next step by preOrderTraversal
   */
  moveNextStep: () => void;
  /**
   * moves active step to previous step by preOrderTraversal
   */
  movePreviousStep: () => void;

  /**
   * Moves active step to next step sibling. Does not move out of common parent
   */
  moveNextSiblingStep: () => void;

  /**
   * Moves active step to next step sibling. Does not move out of common parent
   */
  movePreviousSiblingStep: () => void;

  /**
   * Use recommended worflow step based on activeStepId
   *
   * Cases:
   * - uncompleted leafs => complete current step and move next
   * - uncompleted parents with uncompleted children =>  move to first child uncompleted child
   * - uncompleted parents with all completed children => complete parent and move next
   * - completed step or parent => uncomplete step
   */
  recommendedWorkflowStep: () => void;

  /**
   * toggleCompleted flips the completed state of the node.
   * It also handles any necessary state transitions of dependent nodes (parents and children)
   * These transitions are batched together to be done with a single call to `actions.set`.
   *
   * List of Cases:
   *
   * When completing a node:
   * - All children are set to completed
   *
   * When uncompleting a node:
   * - All ancestors of the node are set to incomplete
   * - All descendants of the node are set to incomplete
   */
  toggleCompleted: (id: string) => void;

  /**
   * setMultipleCompleted set the completed state on multiple nodes. It does not handle dependencies.
   *
   * Favor toggleCompleted to setMultipleCompleted when possible.
   * ToggleCompleted will handle proper ancestor and child manipulation.
   */
  setMultipleCompleted: (ids: string[], value: boolean) => void;
  toggleCollapsed: (id: string) => void;
  addUncollapsed: (id: string) => void;
  setActiveStepId: (id: string) => void;
  setActiveCheckEveryAnnotation: (id: string | null) => void;
  setLockedCheckEveryAnnotation: (id: string | null) => void;
  /**
   * Returns first incomplete child if any.
   * Otherwise, cyclicly loops through siblings until it finds an incomplete one.
   * If all siblings are complete, returns parent.
   *
   * @currentStepid - optional, if set, will find the stepId after this step
   */
  getNextIncompleteStepId: (
    currentStepId?: string,
    opts?: { skipDescendants?: boolean },
  ) => string | null;
  isRootIncomplete: () => boolean;

  getWorkflowName: () => string;

  /**
   * Completes relevant annotationID step when doing CheckEvery given a UUID.
   *
   * If we are not on the current step, does nothing.
   */
  toggleCompletedAnnotationIdByStep: (uuid: string) => void;

  /**
   * checks whether or not a relevant UUID has been completed in the checkEvery step.
   */
  includesAnnotationIdByStep: (uuid: string) => boolean;

  toggleIsGuidedWorkflowInstructionsOpen: () => void;
  toggleIsGuidedWorkflowInstructionsPanelOpen: () => void;
  toggleIsGuidedWorkflowTooltipOpen: () => void;

  isFeedbackModalOpen: boolean;
  toggleIsFeedbackModalOpen: () => void;
}

const useWorkflowHook = (): WorkflowHook => {
  const [completedStepIds, actions] = useList<string>();
  const [uncollapsedStepIds, { push: addUncollapsed, remove: removeUncollapsed }] =
    useList<string>();

  const [activeStepId, setActiveStepIdInternal] = useState<string>('root');

  const [isGuidedWorkflowInstructionsOpen, toggleIsGuidedWorkflowInstructionsOpen] =
    useBoolean(false);
  const [
    isGuidedWorkflowInstructionsPanelOpen,
    toggleIsGuidedWorkflowInstructionsPanelOpenInternal,
  ] = useBoolean(false);

  const [activeCheckEveryAnnotation, setActiveCheckEveryAnnotation] = useState<string | null>(null);

  const [lockedCheckEveryAnnotation, setLockedCheckEveryAnnotation] = useState<string | null>(null);

  const [isGuidedWorkflowTooltipOpen, toggleIsGuidedWorkflowTooltipOpen] = useBoolean(true);
  const [workflow, setWorkflow] = useState<TaskerWorkflow>();

  const [useGuidedWorkflowBar, setUseGuidedWorkflowBar] = useBoolean(false);
  const [isFeedbackModalOpen, toggleIsFeedbackModalOpen] = useBoolean(false);

  // Equivalent to AllSteps
  const [preOrderTraversal, setPreOrderTraversal] = useState<TraversedTaskerWorkflowStep[]>([]);

  // This should never be null
  const currentStep = _.find(preOrderTraversal, step => step.id === activeStepId)!;

  // ****** LOGGING FUNCTIONS ******* //
  const logGuidedWorkflowEvent = useCallback(
    (action: GuidedWorkflowActionTypes): void => {
      const contextKeys = getContextKeys();
      logEvent(Event.GuidedWorkflowActions, {
        subtaskId: contextKeys.includes('subtaskId') ? getContextValue('subtaskId') : null,
        projectId: contextKeys.includes('projectId') ? getContextValue('projectId') : null,
        version: 2.0,
        activeStepMetadata: {
          stepId: currentStep.id,
          stepType: !currentStep.steps || currentStep.steps.length === 0 ? 'leaf' : 'parent',
        },
        actionType: action,
        numCompletedSteps: completedStepIds.length,
        numTotalSteps: preOrderTraversal?.length,
      });
    },
    [completedStepIds.length, currentStep, preOrderTraversal],
  );

  const setActiveStepId = useCallback(
    (id: string) => {
      setActiveStepIdInternal(id);
      logGuidedWorkflowEvent(GuidedWorkflowActionTypes.ActiveStepIdChanged);
    },
    [logGuidedWorkflowEvent],
  );

  const toggleCompleted = useCallback(
    (id: string) => {
      if (completedStepIds.includes(id)) {
        const nodeAndAncestorsIds = getWorkflowAncestors(workflow!, id).map(node => node.id);
        const nodeAndChildrenIds = getNodeChildren(workflow!, id).map(node => node.id);

        const newCompletedIds = _.difference(completedStepIds, [
          ...nodeAndAncestorsIds,
          ...nodeAndChildrenIds,
        ]);
        actions.set(newCompletedIds);
        logGuidedWorkflowEvent(GuidedWorkflowActionTypes.WorkflowStepUncompleted);
      } else {
        const nodeAndChildrenIds = getNodeChildren(workflow!, id).map(node => node.id);
        if (!nodeAndChildrenIds.length) {
          // Nothing to do since the id referenced a workflow step that isn't loaded
          return;
        }
        const newCompletedIds = _.uniq([...completedStepIds, ...nodeAndChildrenIds]);

        actions.set(newCompletedIds);
        logGuidedWorkflowEvent(GuidedWorkflowActionTypes.WorkflowStepCompleted);
      }
    },
    [actions, completedStepIds, logGuidedWorkflowEvent, workflow],
  );

  const setMultipleCompleted = useCallback(
    (ids: string[], value: boolean) => {
      if (value) {
        const uncompletedIds = _.filter(ids, o => {
          return !completedStepIds.includes(o);
        });
        actions.set(completedStepIds.concat(uncompletedIds));
      } else {
        const newCompletedIds = _.filter(completedStepIds, o => {
          return !ids.includes(o);
        });
        actions.set(newCompletedIds);
      }
    },
    [actions, completedStepIds],
  );

  const toggleCollapsed = useCallback(
    (id: string) => {
      if (uncollapsedStepIds.includes(id)) {
        const idx = uncollapsedStepIds.indexOf(id);
        removeUncollapsed(idx);
      } else {
        addUncollapsed(id);
      }
    },
    [addUncollapsed, removeUncollapsed, uncollapsedStepIds],
  );

  const toggleCompletedAnnotationIdByStep = useCallback(
    (uuid: string) => {
      // if it's the parent node, use it's own ID. Else, find the parent nodes ID
      const currentStepParent = currentStep?.linkedAnnotationId
        ? currentStep?.id.slice(0, -5)
        : currentStep?.id;
      const associatedStepId = currentStepParent + '-' + getFriendlyAnnotationId(uuid);

      toggleCompleted(associatedStepId);
    },
    [currentStep, toggleCompleted],
  );

  const includesAnnotationIdByStep = useCallback(
    (uuid: string) => {
      const currentStepParent = currentStep?.linkedAnnotationId
        ? currentStep?.id.slice(0, -5)
        : currentStep?.id;
      const associatedStepId = currentStepParent + '-' + getFriendlyAnnotationId(uuid);

      return completedStepIds.includes(associatedStepId);
    },
    [completedStepIds, currentStep],
  );

  const updateWorkflow = useCallback(
    (newWorkflow: any) => {
      if (!newWorkflow) {
        setWorkflow(newWorkflow);
        setPreOrderTraversal([]);
        return;
      }

      const allNodeIds = newWorkflow
        ? _.map(getPreorderWorkflowTraversal(newWorkflow), node => node.id)
        : [];

      // Set new activeStepID
      if (workflow && !_.includes(allNodeIds, activeStepId)) {
        const oldActiveStepAncestorIds = getWorkflowAncestors(workflow, activeStepId).map(
          node => node.id,
        );
        const firstCommonAncestor = _.find(oldActiveStepAncestorIds, ancestorId =>
          allNodeIds.includes(ancestorId),
        );
        setActiveStepId(firstCommonAncestor ?? 'root');
      }

      const oldNodeIds = workflow
        ? _.map(getPreorderWorkflowTraversal(workflow), node => node.id)
        : [];

      // Check if any nodes have been deleted or added (label changes)
      if (
        _.difference(completedStepIds, allNodeIds).length !== 0 ||
        _.difference(allNodeIds, oldNodeIds).length !== 0
      ) {
        // filter out any ids that no longer exist
        const deletedNodeIds = _.filter(completedStepIds, id => !allNodeIds.includes(id));

        // find new Ids and handle parent/child dependencies
        const newNodeIds = _.filter(allNodeIds, id => !oldNodeIds.includes(id));
        const allNewNodeIdsAncestors = _.flatten(
          _.map(newNodeIds, id => getWorkflowAncestors(newWorkflow, id).map(node => node.id)),
        );
        const allNewNodeIdsChildren = _.flatten(
          _.map(newNodeIds, id => getNodeChildren(newWorkflow, id).map(node => node.id)),
        );

        const newCompletedIds = _.difference(completedStepIds, [
          ...deletedNodeIds,
          ...allNewNodeIdsAncestors,
          ...allNewNodeIdsChildren,
        ]);

        setPreOrderTraversal(getPreorderWorkflowTraversal(newWorkflow));
        actions.set(newCompletedIds);
      }

      setWorkflow(newWorkflow);
    },
    [actions, activeStepId, completedStepIds, setActiveStepId, workflow],
  );

  const getNextStepId = useCallback((): string | null => {
    if (!workflow) {
      return null;
    }
    const currentStepIdx = _.findIndex(preOrderTraversal, step => step.id === activeStepId)!;

    const nextStepId =
      currentStepIdx === -1
        ? null
        : currentStepIdx + 1 < preOrderTraversal.length
        ? preOrderTraversal[currentStepIdx + 1].id
        : null;

    return nextStepId;
  }, [activeStepId, preOrderTraversal, workflow]);

  const getNextSiblingId = useCallback((): string | null => {
    const parentId = currentStep.id.slice(0, currentStep.id.length - (currentStep.name.length + 1));

    const parent =
      currentStep.depth === 1
        ? _.find(preOrderTraversal, step => step.id === 'root')
        : _.find(preOrderTraversal, step => step.id === parentId);

    if (!parent) {
      return null;
    }

    const siblings = parent.steps;
    if (!siblings) {
      return null;
    }

    const currentSiblingIndex = _.findIndex(siblings, s => s.id === activeStepId);
    const nextSiblingId =
      currentSiblingIndex === -1
        ? null
        : currentSiblingIndex + 1 < siblings.length
        ? siblings[currentSiblingIndex + 1].id
        : null;

    return nextSiblingId;
  }, [activeStepId, currentStep, preOrderTraversal]);

  const getPreviousStepId = useCallback((): string | null => {
    if (!workflow) {
      return null;
    }
    const currentStepIdx = _.findIndex(preOrderTraversal, step => step.id === activeStepId)!;

    const nextPreviousId =
      currentStepIdx === -1
        ? null
        : currentStepIdx - 1 >= 0
        ? preOrderTraversal[currentStepIdx - 1].id
        : null;

    return nextPreviousId;
  }, [activeStepId, preOrderTraversal, workflow]);

  const getPreviousSiblingId = useCallback((): string | null => {
    const parentId = currentStep.id.slice(0, currentStep.id.length - (currentStep.name.length + 1));

    const parent =
      currentStep.depth === 1
        ? _.find(preOrderTraversal, step => step.id === 'root')
        : _.find(preOrderTraversal, step => step.id === parentId);

    if (!parent) {
      return null;
    }

    const siblings = parent.steps;
    if (!siblings) {
      return null;
    }

    const currentSiblingIndex = _.findIndex(siblings, s => s.id === activeStepId);
    const previousSiblingId =
      currentSiblingIndex === -1
        ? null
        : currentSiblingIndex - 1 >= 0
        ? siblings[currentSiblingIndex - 1].id
        : null;

    return previousSiblingId;
  }, [activeStepId, currentStep, preOrderTraversal]);

  const getNextIncompleteStepId = useCallback(
    (currentStepId?: string, opts?: { skipDescendants?: boolean }): string | null => {
      if (!workflow) {
        return null;
      }

      const allSteps = getPreorderWorkflowTraversal(workflow);
      const currentStep = _.find(allSteps, step => step.id === (currentStepId ?? activeStepId));
      if (!currentStep) {
        return null;
      }

      const firstIncompleteChild = _.find(
        currentStep.steps,
        step => !completedStepIds.includes(step.id),
      );
      if (!opts?.skipDescendants && firstIncompleteChild) {
        // If there are incomplete children, go to the first incomplete child
        return firstIncompleteChild.id;
      }

      const path = getWorkflowAncestors(workflow, currentStepId ?? activeStepId).map(
        node => node.id,
      );
      if (path.length < 2) return null;
      const parent = path[path.length - 2];
      const siblings = allSteps.find(step => step.id === parent)!.steps!.map(step => step.id);
      const index = siblings.indexOf(currentStepId ?? activeStepId);
      const orderedSiblings = [...siblings.slice(index + 1), ...siblings.slice(0, index)];
      return orderedSiblings.find(id => !completedStepIds.includes(id)) ?? parent;
    },
    [activeStepId, completedStepIds, workflow],
  );

  const isRootIncomplete = useCallback(() => {
    const rootStep = _.first(preOrderTraversal);
    if (!rootStep) {
      return false;
    }
    return !completedStepIds.includes(rootStep?.id);
  }, [preOrderTraversal, completedStepIds]);

  const getWorkflowName = useCallback(() => {
    const rootStep = _.first(preOrderTraversal);
    return rootStep?.name || 'Guided Workflow';
  }, [preOrderTraversal]);

  // ***** WORKFLOW MANIPULATION FUNCTIONS ******
  const completeAndMoveNext = useCallback((): void => {
    if (!activeStepId) return;

    logGuidedWorkflowEvent(GuidedWorkflowActionTypes.ClickCompleteButton);

    toggleCompleted(activeStepId);

    const nextIncompleteStepId = getNextIncompleteStepId(activeStepId, { skipDescendants: true });
    if (!nextIncompleteStepId) return;
    setActiveStepId(nextIncompleteStepId);
  }, [
    activeStepId,
    getNextIncompleteStepId,
    logGuidedWorkflowEvent,
    setActiveStepId,
    toggleCompleted,
  ]);

  const moveNextStep = useCallback((): void => {
    logGuidedWorkflowEvent(GuidedWorkflowActionTypes.ClickForwardButton);

    const nextStepId = getNextStepId();
    if (!activeStepId || !nextStepId) return;
    setActiveStepId(nextStepId);
  }, [activeStepId, getNextStepId, logGuidedWorkflowEvent, setActiveStepId]);

  const moveNextSiblingStep = useCallback((): void => {
    const nextSiblingId = getNextSiblingId();

    if (!activeStepId || !nextSiblingId) return;
    setActiveStepId(nextSiblingId);
  }, [activeStepId, getNextSiblingId, setActiveStepId]);

  const movePreviousStep = useCallback((): void => {
    logGuidedWorkflowEvent(GuidedWorkflowActionTypes.ClickBackwardButton);

    const previousStepId = getPreviousStepId();
    if (!activeStepId || !previousStepId) return;
    setActiveStepId(previousStepId);
  }, [activeStepId, getPreviousStepId, logGuidedWorkflowEvent, setActiveStepId]);

  const movePreviousSiblingStep = useCallback((): void => {
    const previousSiblingId = getPreviousSiblingId();
    if (!activeStepId || !previousSiblingId) return;
    setActiveStepId(previousSiblingId);
  }, [activeStepId, getPreviousSiblingId, setActiveStepId]);

  const recommendedWorkflowStep = useCallback((): void => {
    if (!workflow) {
      return;
    }
    if (completedStepIds.includes(activeStepId)) {
      toggleCompleted(activeStepId);
      return;
    }

    const nextIncompleteStepId = getNextIncompleteStepId();
    const hasIncompleteChildren = !!_.find(
      currentStep?.steps,
      step => step.id === nextIncompleteStepId,
    );

    if (hasIncompleteChildren) {
      moveNextStep();
      return;
    }

    completeAndMoveNext();
  }, [
    activeStepId,
    completeAndMoveNext,
    completedStepIds,
    currentStep,
    getNextIncompleteStepId,
    moveNextStep,
    toggleCompleted,
    workflow,
  ]);

  const toggleIsGuidedWorkflowInstructionsPanelOpen = useCallback(() => {
    if (isGuidedWorkflowInstructionsPanelOpen) {
      logGuidedWorkflowEvent(GuidedWorkflowActionTypes.InstructionPanelClose);
    } else {
      logGuidedWorkflowEvent(GuidedWorkflowActionTypes.InstructionPanelOpen);
    }
    toggleIsGuidedWorkflowInstructionsPanelOpenInternal();
  }, [
    isGuidedWorkflowInstructionsPanelOpen,
    logGuidedWorkflowEvent,
    toggleIsGuidedWorkflowInstructionsPanelOpenInternal,
  ]);

  const workflowState = useMemo(
    () => ({
      completedStepIds,
      uncollapsedStepIds,
      activeStepId,
      currentStep,
      isGuidedWorkflowInstructionsOpen,
      isGuidedWorkflowTooltipOpen,
      isGuidedWorkflowInstructionsPanelOpen,
      activeCheckEveryAnnotation,
      lockedCheckEveryAnnotation,
    }),
    [
      completedStepIds,
      uncollapsedStepIds,
      activeStepId,
      currentStep,
      isGuidedWorkflowInstructionsOpen,
      isGuidedWorkflowTooltipOpen,
      isGuidedWorkflowInstructionsPanelOpen,
      activeCheckEveryAnnotation,
      lockedCheckEveryAnnotation,
    ],
  );

  return useMemo(
    () => ({
      workflow,
      updateWorkflow,
      useGuidedWorkflowBar,
      setUseGuidedWorkflowBar,
      workflowState,
      toggleCompleted,
      setMultipleCompleted,
      addUncollapsed,
      toggleCollapsed,
      setActiveStepId,
      setActiveCheckEveryAnnotation,
      setLockedCheckEveryAnnotation,
      getNextIncompleteStepId,
      toggleIsGuidedWorkflowInstructionsOpen,
      toggleIsGuidedWorkflowTooltipOpen,
      completeAndMoveNext,
      moveNextStep,
      moveNextSiblingStep,
      movePreviousStep,
      movePreviousSiblingStep,
      recommendedWorkflowStep,
      toggleIsGuidedWorkflowInstructionsPanelOpen,
      toggleCompletedAnnotationIdByStep,
      includesAnnotationIdByStep,
      getWorkflowName,
      isRootIncomplete,
      isFeedbackModalOpen,
      toggleIsFeedbackModalOpen,
    }),
    [
      workflow,
      updateWorkflow,
      useGuidedWorkflowBar,
      setUseGuidedWorkflowBar,
      workflowState,
      toggleCompleted,
      setMultipleCompleted,
      addUncollapsed,
      toggleCollapsed,
      setActiveStepId,
      getNextIncompleteStepId,
      toggleIsGuidedWorkflowInstructionsOpen,
      toggleIsGuidedWorkflowTooltipOpen,
      completeAndMoveNext,
      moveNextStep,
      moveNextSiblingStep,
      movePreviousStep,
      movePreviousSiblingStep,
      recommendedWorkflowStep,
      toggleIsGuidedWorkflowInstructionsPanelOpen,
      toggleCompletedAnnotationIdByStep,
      includesAnnotationIdByStep,
      isRootIncomplete,
      getWorkflowName,
      isFeedbackModalOpen,
      toggleIsFeedbackModalOpen,
    ],
  );
};

export default useWorkflowHook;
